import type { PalavraCanonica } from '../tipos';
import { paraComparacao } from '../lexico/normalizacao';

/**
 * Avaliação de um palpite (§23.3).
 *
 * `certa` é letra na posição certa, `presente` é letra que existe na resposta
 * mas noutro lugar, `ausente` é letra que não existe — ou que já foi gasta.
 *
 * Essa última ressalva é onde quase toda implementação de Wordle erra. Se a
 * resposta tem **um** `A` e o palpite tem **dois**, só um deles pode acender:
 * o outro é `ausente`, senão o jogo diria que há dois `A` na resposta. Daí as
 * duas passadas — primeiro as certas, que consomem suas letras; só o que
 * sobra fica disponível para as presentes.
 *
 * A comparação ignora diacríticos (§23.3): `CORACAO` avalia contra `CORAÇÃO`
 * com todas as letras certas. É exceção deliberada a §4.3, e vale só aqui —
 * num jogo de dedução, exigir o acento faria o amarelo mentir.
 *
 * Palpite e resposta podem ter tamanhos diferentes: na Trama a palavra
 * escrita é livre de 1 a 12 letras (§23.3), e é a palavra do dia que tem
 * tamanho fixo. Só posição que existe nas duas pode ficar verde; da posição
 * seguinte em diante, uma letra só chega a amarelo.
 */

export type EstadoLetra = 'certa' | 'presente' | 'ausente';

export function avaliar(
  palpite: PalavraCanonica | string,
  resposta: PalavraCanonica | string,
): readonly EstadoLetra[] {
  const p = [...paraComparacao(palpite)];
  const r = [...paraComparacao(resposta)];

  const saida: EstadoLetra[] = new Array(p.length).fill('ausente');

  /* Quais letras da resposta já foram consumidas por uma certa. */
  const consumida: boolean[] = new Array(r.length).fill(false);

  // Primeira passada: as certas. Elas têm prioridade sobre as presentes e
  // consomem a letra correspondente antes de qualquer outra reivindicá-la.
  for (let i = 0; i < p.length; i += 1) {
    if (i < r.length && p[i] === r[i]) {
      saida[i] = 'certa';
      consumida[i] = true;
    }
  }

  /*
   * Quantas vezes cada letra da resposta ainda está disponível.
   *
   * A contagem percorre a **resposta inteira**, e não só até o tamanho do
   * palpite: na Trama as palavras têm tamanho livre (§23.3), e um
   * palpite curto precisa poder acender amarelo por uma letra que está lá no
   * fim da resposta.
   */
  const disponiveis = new Map<string, number>();
  for (let i = 0; i < r.length; i += 1) {
    if (consumida[i]) continue;
    const letra = r[i];
    if (letra === undefined) continue;
    disponiveis.set(letra, (disponiveis.get(letra) ?? 0) + 1);
  }

  // Segunda passada: as presentes, enquanto houver letra sobrando.
  for (let i = 0; i < p.length; i += 1) {
    if (saida[i] === 'certa') continue;
    const letra = p[i];
    if (letra === undefined) continue;

    const resta = disponiveis.get(letra) ?? 0;
    if (resta > 0) {
      saida[i] = 'presente';
      disponiveis.set(letra, resta - 1);
    }
  }

  return saida;
}

export function acertou(estados: readonly EstadoLetra[]): boolean {
  return estados.length > 0 && estados.every((e) => e === 'certa');
}

/** Ordem de força: uma tecla nunca volta de `certa` para `presente`. */
const FORCA: Readonly<Record<EstadoLetra, number>> = { ausente: 1, presente: 2, certa: 3 };

/**
 * O que já se sabe de cada letra, para pintar o teclado (§23.4).
 *
 * Guarda sempre o melhor estado observado: descobrir que o `A` está na posição
 * certa não pode ser desfeito por um palpite posterior em que ele caiu no
 * lugar errado.
 */
export function estadoDasTeclas(
  tentativas: readonly { readonly texto: string; readonly letras: readonly EstadoLetra[] }[],
): ReadonlyMap<string, EstadoLetra> {
  const mapa = new Map<string, EstadoLetra>();

  for (const tentativa of tentativas) {
    const letras = [...paraComparacao(tentativa.texto)];
    for (let i = 0; i < letras.length; i += 1) {
      const letra = letras[i];
      const estado = tentativa.letras[i];
      if (letra === undefined || estado === undefined) continue;

      const atual = mapa.get(letra);
      if (atual === undefined || FORCA[estado] > FORCA[atual]) mapa.set(letra, estado);
    }
  }

  return mapa;
}
